import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { useAppContext } from "../../../../AppContext";
import { IDeletableEventModel } from "../models/deletableEventModel";
import {
  buildEventPickerOption,
  IEventPickerOption,
} from "../utils/eventPickerOptionUtils";
import {
  bulkDeleteEventsFormDefaultValues,
  bulkDeleteEventsFormSchema,
  IBulkDeleteEventsFormModel,
} from "../models/bulkDeleteEventsFormModel";

interface IUseBulkDeleteEventsDialogProps {
  visible: boolean;
  onHide: () => void;
}

export const formatEventsCount = (count: number): string =>
  count === 1 ? "1 събитие" : `${count} събития`;

export const formatReservationsCount = (count: number): string =>
  count === 1 ? "1 резервация" : `${count} резервации`;

/** The picker row, with what deleting the event would cancel on its second line. */
const buildDeletableEventOption = (
  event: IDeletableEventModel,
): IEventPickerOption => {
  const details: string[] = [];

  if (event.willNotTakePlace) {
    details.push("няма да се проведе");
  }

  if (event.reservationsCount > 0) {
    details.push(formatReservationsCount(event.reservationsCount));
  }

  return buildEventPickerOption(event, details);
};

const buildConfirmMessage = (
  eventsCount: number,
  reservationsCount: number,
): string => {
  const reservationsPart =
    reservationsCount > 0
      ? ` В тях има ${formatReservationsCount(reservationsCount)}, които ще бъдат отменени.` +
        " Платеното с посещенията от карти и депозитите се връща на клиентите;" +
        " платеното в брой, с банкова карта или през Stripe не се връща автоматично." +
        " Клиентите няма да получат известие."
      : "";

  return (
    `Ще изтриете ${formatEventsCount(eventsCount)}.${reservationsPart}` +
    " Наистина ли искате да продължите?"
  );
};

export const useBulkDeleteEventsDialog = ({
  visible,
  onHide,
}: IUseBulkDeleteEventsDialogProps) => {
  const { selectedStudioId } = useAppContext();

  const form = useForm<IBulkDeleteEventsFormModel>({
    defaultValues: bulkDeleteEventsFormDefaultValues,
    resolver: yupResolver(bulkDeleteEventsFormSchema),
    mode: "onChange",
  });

  const watchedEventIds = form.watch("eventIds");

  useEffect(() => {
    if (!visible) {
      form.reset(bulkDeleteEventsFormDefaultValues);
    }
  }, [visible, form]);

  const { data: deletableEvents, isLoading: isQueryLoading } = useApiQuery<
    IDeletableEventModel[]
  >({
    queryKey: [endpointsMetadata.planner.getDeletableEvents, selectedStudioId],
    endpoint: endpointsMetadata.planner.getDeletableEvents,
    enabled: visible,
    globalIsLoading: false,
    disableErrorToast: false,
    retry: false,
  });

  // React Query reports a DISABLED query as loading; only a running one counts here.
  const isLoadingEvents = visible && isQueryLoading;

  const eventOptions: IEventPickerOption[] = useMemo(
    () => deletableEvents?.map(buildDeletableEventOption) ?? [],
    [deletableEvents],
  );

  const selectedEventIds = useMemo(
    () => watchedEventIds ?? [],
    [watchedEventIds],
  );

  // A Set, because "select all" can tick thousands of events against a list of thousands.
  const selectedReservationsCount = useMemo(() => {
    if (!deletableEvents || selectedEventIds.length === 0) {
      return 0;
    }

    const selected = new Set(selectedEventIds);
    return deletableEvents
      .filter((event) => selected.has(event.id))
      .reduce((sum, event) => sum + event.reservationsCount, 0);
  }, [deletableEvents, selectedEventIds]);

  const { mutate, isLoading: isDeleting } = useApiMutation();

  const selectedCount = selectedEventIds.length;
  const canDelete = selectedCount > 0;

  const deleteSelected = () => {
    // Closed right away: the global loader renders beneath the dialog, so leaving it open
    // would hide the progress of a long delete. The hook stays mounted while the dialog is
    // hidden, so the request carries on and its toast still shows.
    onHide();

    mutate({
      endpoint: endpointsMetadata.planner.bulkDeleteEvents,
      method: "post",
      globalIsLoading: true,
      payload: { eventIds: selectedEventIds },
      invalidateKeys: [
        [endpointsMetadata.planner.getAll],
        [endpointsMetadata.planner.getDeletableEvents],
      ],
    });
  };

  const handleDelete = () => {
    if (!canDelete) {
      return;
    }

    showCustomConfirmDialog(
      buildConfirmMessage(selectedCount, selectedReservationsCount),
      deleteSelected,
      () => {},
      "warning",
      "Да, изтрий",
      "Не",
    );
  };

  return {
    form,
    eventOptions,
    isLoadingEvents,
    selectedCount,
    selectedReservationsCount,
    canDelete,
    isDeleting,
    handleDelete,
  };
};
